'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { typographyVariants } from './typography'

const dialogSurfaceVariants = cva(
  'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:pointer-events-none fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-3xl shadow-xl ring-1 ring-foreground/10 duration-200',
  {
    variants: {
      size: {
        compact: 'sm:max-w-md',
        small: 'sm:max-w-lg',
        default: 'sm:max-w-xl',
        large: 'sm:max-w-2xl',
        xlarge: 'sm:max-w-3xl',
        '2xlarge': 'sm:max-w-4xl',
        '3xlarge': 'sm:max-w-5xl',
        wide: 'sm:max-w-7xl',
        canvas: 'sm:max-w-[min(72rem,calc(100vw-3rem))]',
      },
      viewport: {
        auto: '',
        scroll: 'max-h-[calc(100dvh-2rem)] overflow-y-auto',
        contained: 'max-h-[calc(100dvh-2rem)] overflow-hidden',
      },
      padding: {
        none: 'p-0',
        compact: 'p-4 sm:p-5',
        tight: 'p-5',
        default: 'p-6',
      },
      spacing: {
        none: 'gap-0',
        compact: 'gap-3',
        default: 'gap-5',
      },
    },
    defaultVariants: {
      size: 'default',
      viewport: 'auto',
      padding: 'default',
      spacing: 'default',
    },
  }
)

const dialogOverlayClassName =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0 fixed inset-0 z-50 bg-[rgba(46,45,47,0.48)] backdrop-blur-[3px]'

export type DialogProps = ComponentProps<typeof DialogPrimitive.Root>
export type DialogTriggerProps = ComponentProps<typeof DialogPrimitive.Trigger>
export type DialogPortalProps = ComponentProps<typeof DialogPrimitive.Portal>
export type DialogCloseProps = ComponentProps<typeof DialogPrimitive.Close>
export type DialogOverlayProps = ComponentProps<typeof DialogPrimitive.Overlay>
export type DialogBackdropProps = ComponentProps<'div'>
export type DialogContentProps = Omit<
  ComponentProps<typeof DialogPrimitive.Content>,
  'forceMount'
> &
  VariantProps<typeof dialogSurfaceVariants> & {
    forceMount?: boolean
    showCloseButton?: boolean
  }
export type DialogHeaderProps = ComponentProps<'div'>
export type DialogFooterProps = ComponentProps<'div'>
export type DialogTitleProps = ComponentProps<typeof DialogPrimitive.Title>
export type DialogDescriptionProps = ComponentProps<typeof DialogPrimitive.Description>

function Dialog({ ...props }: DialogProps) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogTriggerProps) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPortalProps) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogCloseProps) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className = '', ...props }: DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(dialogOverlayClassName, className)}
      {...props}
    />
  )
}

function DialogBackdrop({ className = '', ...props }: DialogBackdropProps) {
  return (
    <DialogPortal>
      <div
        aria-hidden="true"
        data-slot="dialog-overlay"
        data-state="open"
        className={cn(dialogOverlayClassName, className)}
        {...props}
      />
    </DialogPortal>
  )
}

function DialogContent({
  className = '',
  children,
  showCloseButton = true,
  forceMount = false,
  hidden = false,
  padding = 'default',
  size = 'default',
  spacing = 'default',
  viewport = 'auto',
  ...props
}: DialogContentProps) {
  // Radix types forceMount as `true | undefined`; false and undefined behave identically.
  const radixForceMount = forceMount || undefined

  return (
    <DialogPortal data-slot="dialog-portal" forceMount={radixForceMount}>
      <DialogOverlay forceMount={radixForceMount} className={hidden ? '!hidden' : ''} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-padding={padding}
        data-size={size}
        data-spacing={spacing}
        data-viewport={viewport}
        forceMount={radixForceMount}
        hidden={hidden}
        className={cn(
          hidden ? '!hidden' : '',
          dialogSurfaceVariants({ padding, size, spacing, viewport }),
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className = '', ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({ className = '', ...props }: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  )
}

function DialogTitle({ className = '', ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(typographyVariants({ variant: 'sectionTitle' }), className)}
      {...props}
    />
  )
}

function DialogDescription({ className = '', ...props }: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(typographyVariants({ variant: 'description' }), className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  dialogSurfaceVariants,
}
